import { useEffect, useMemo, useState } from 'react';
import { api, fmt } from '../api.ts';

interface OutsideStage { jobStageId: number; stageId: number; name: string; status: string; canPlan: boolean; tripId: number | null }
interface Trip {
  id: number; status: 'PLANNED' | 'OUT'; worker: string; place: string | null; reason: string | null;
  plannedQuantity: number | null; sentQuantity: number | null; stageNames: string; jobStageIds: number[]; workerId: number;
}
interface JobOutside {
  job: { id: number; jobNumber: string; status: string; customer: string; box: string; orderedQuantity: number; productionQuantity: number };
  stages: OutsideStage[];
  trips: Trip[];
}
interface Worker { id: number; name: string; place: string | null }

const REASONS = ['Machine down', 'Busy', 'Size not doable', "Don't have the machine"];
const workerLabel = (w: Worker) => `${w.name}${w.place ? ` · ${w.place}` : ''}`;

/**
 * Where is each stage of a job done: in OPAL, or by a job worker.
 *
 * One row per stage, each with its own choice, so a whole order can go
 * elsewhere, a single stage can, a few can, or different stages can go to
 * different places. "Set all to" is the whole-order shortcut. The quantity is
 * for the plan as a whole: a part goes through every stage marked outside.
 *
 * Saving replaces the plan and changes nothing on the floor: the job stays one
 * job until the stage is reached in Production, where Send out confirms any split.
 */
export default function SendOutsideDialog({ jobId, preselect, onClose, onDone }: {
  jobId: number;
  /** From a Production row the stage is known, so only that row shows until asked for the rest. */
  preselect?: number;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [data, setData] = useState<JobOutside | null>(null);
  const [workers, setWorkers] = useState<Worker[]>([]);
  /** jobStageId -> worker id, or '' / absent for In OPAL. */
  const [where, setWhere] = useState<Record<number, string>>({});
  const [reason, setReason] = useState('');
  const [part, setPart] = useState(false);
  const [qty, setQty] = useState('');
  const [every, setEvery] = useState(!preselect);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [d, w] = await Promise.all([
          api.get<JobOutside>(`/api/jobs/${jobId}/outside`),
          api.get<{ workers: Worker[] }>('/api/outside/workers'),
        ]);
        if (!live) return;
        const planned = d.trips.filter((t) => t.status === 'PLANNED');
        const start: Record<number, string> = {};
        for (const t of planned) for (const id of t.jobStageIds) start[id] = String(t.workerId);
        setData(d); setWorkers(w.workers); setWhere(start);
        setReason(planned[0]?.reason ?? '');
        const pq = planned[0]?.plannedQuantity ?? null;
        setPart(pq !== null); setQty(pq !== null ? String(pq) : '');
      } catch (e) {
        if (live) setError(e instanceof Error ? e.message : 'Could not load the job');
      }
    })();
    return () => { live = false; };
  }, [jobId]);

  const ordered = data?.job.orderedQuantity ?? 0;
  const out = useMemo(() => new Map((data?.trips ?? []).filter((t) => t.status === 'OUT')
    .flatMap((t) => t.jobStageIds.map((id) => [id, t] as const))), [data]);
  const rows = (data?.stages ?? []).filter((s) => s.status === 'PENDING' || out.has(s.jobStageId));
  const editable = rows.filter((s) => !out.has(s.jobStageId) && (s.canPlan || where[s.jobStageId] !== undefined));
  const isEditable = (id: number) => editable.some((e) => e.jobStageId === id);
  const shown = !every && preselect ? rows.filter((s) => s.jobStageId === preselect) : rows;
  const hasPlan = (data?.trips ?? []).some((t) => t.status === 'PLANNED');
  const goingOut = editable.filter((s) => where[s.jobStageId]).length;
  const someOut = out.size > 0;

  const q = Number(qty);
  const qtyOk = !part || (qty.trim() !== '' && Number.isInteger(q) && q >= 1 && q < ordered);
  const qtyProblem = part && qty.trim() !== '' && !qtyOk ? `A part is less than ${fmt(ordered)}` : '';
  const valid = (goingOut > 0 || hasPlan) && (goingOut === 0 || qtyOk);

  const setStage = (id: number, v: string) => setWhere((p) => ({ ...p, [id]: v }));
  const setAllTo = (v: string) => setWhere(Object.fromEntries(editable.map((s) => [s.jobStageId, v])));

  async function save() {
    if (!valid || !data) return;
    setBusy(true); setError('');
    try {
      await api.put(`/api/jobs/${jobId}/outside`, {
        stages: editable.map((s) => ({ jobStageId: s.jobStageId, workerId: where[s.jobStageId] ? Number(where[s.jobStageId]) : null })),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        quantity: part && goingOut > 0 ? q : null,
      });
      const names = editable.filter((s) => where[s.jobStageId]);
      const who = [...new Set(names.map((s) => where[s.jobStageId]))];
      const one = workers.find((w) => String(w.id) === who[0]);
      onDone(names.length === 0 ? 'Outside plan cleared'
        : names.length === 1 ? `${names[0]!.name} planned to go to ${one?.name ?? 'the job worker'}${part ? ` · ${fmt(q)} of ${fmt(ordered)}` : ''}`
        : `${names.length} stages planned outside${part ? ` · ${fmt(q)} of ${fmt(ordered)}` : ''}`);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the plan');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="backdrop" onClick={() => !busy && onClose()}>
      <div className="modal" style={{ width: 560 }} role="dialog" aria-label="Send outside" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Send outside{data ? ` · ${data.job.jobNumber}` : ''}</h3>
          {data && <p>{data.job.customer} · {data.job.box} · {fmt(ordered)}</p>}
        </div>
        <div className="modal-body">
          {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
          {!data && !error && <div className="dim">Loading…</div>}

          {data && (
            <>
              <div className="out-top">
                <span className="field-label" style={{ margin: 0 }}>Where is each stage done</span>
                {editable.length > 1 && (
                  <div className="out-setall">
                    <span>Set all to</span>
                    <div className="field out-select" style={{ width: 200 }}>
                      <select aria-label="Set all stages to" value=""
                              onChange={(e) => { if (e.target.value !== '') setAllTo(e.target.value === 'OPAL' ? '' : e.target.value); }}>
                        <option value="">Choose…</option>
                        <option value="OPAL">In OPAL</option>
                        {workers.map((w) => <option key={w.id} value={w.id}>{workerLabel(w)}</option>)}
                      </select>
                    </div>
                  </div>
                )}
              </div>
              <div className="out-rows">
                {shown.map((s) => {
                  const lockedTrip = out.get(s.jobStageId);
                  const v = where[s.jobStageId] ?? '';
                  return (
                    <div key={s.jobStageId} className={`out-row${v || lockedTrip ? ' out' : ''}`}>
                      <span className="out-n">{rows.indexOf(s) + 1}</span>
                      <span className="out-name">{s.name}</span>
                      {lockedTrip ? <span className="dim out-fixed">At {lockedTrip.worker}</span>
                        : !isEditable(s.jobStageId) ? <span className="dim out-fixed">Always from OPAL</span>
                        : (
                          <div className="field out-select">
                            <select aria-label={`${s.name} done by`} value={v} onChange={(e) => setStage(s.jobStageId, e.target.value)}>
                              <option value="">In OPAL</option>
                              {workers.map((w) => <option key={w.id} value={w.id}>{workerLabel(w)}</option>)}
                            </select>
                          </div>
                        )}
                    </div>
                  );
                })}
                {!every && preselect && rows.length > 1 && (
                  <div className="out-more">
                    <button type="button" className="linkish" onClick={() => setEvery(true)}>Other stages…</button>
                  </div>
                )}
              </div>
              {workers.length === 0 && <div className="hint" style={{ marginBottom: 10 }}>Add job workers in Settings first.</div>}

              <div className="field-pair">
                <div className="field" style={{ marginBottom: 0 }}>
                  <label>How many go outside</label>
                  <div className="seg" role="group" aria-label="How many go outside" style={{ marginBottom: part ? 8 : 0 }}>
                    <button type="button" aria-current={!part} onClick={() => setPart(false)}>All {fmt(ordered)}</button>
                    <button type="button" aria-current={part} disabled={someOut}
                            title={someOut ? 'Some of it is already out' : undefined}
                            onClick={() => setPart(true)}>Part</button>
                  </div>
                  {part && (
                    <>
                      <input type="number" min="1" step="1" aria-label="Quantity going outside" value={qty} autoFocus
                             style={{ width: 120 }} onChange={(e) => setQty(e.target.value)} />
                      <span className="dim"> of {fmt(ordered)}</span>
                      {qtyProblem && <div className="hint pp-warn">{qtyProblem}</div>}
                    </>
                  )}
                </div>
                <div className="field" style={{ marginBottom: 0 }}>
                  <label htmlFor="out-reason">Reason (optional)</label>
                  <input id="out-reason" type="text" list="out-reasons" value={reason} maxLength={200}
                         onChange={(e) => setReason(e.target.value)} />
                  <datalist id="out-reasons">{REASONS.map((r) => <option key={r} value={r} />)}</datalist>
                </div>
              </div>
              <div className="hint out-note">
                Saved as a plan. {data.job.jobNumber} stays one job until the stage is reached in Production,
                where you confirm before it splits.{part ? ' A part carries all its outside stages with it.' : ''}
              </div>
            </>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={() => void save()} disabled={busy || !valid}>
            {busy ? 'Saving…' : goingOut === 0 && hasPlan ? 'Clear plan' : 'Save plan'}
          </button>
        </div>
      </div>
    </div>
  );
}
